import React, { useState,useEffect } from "react";
import { toast } from 'react-toastify';
import {format } from 'date-fns';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getDefaultReviewList,getSingleReviewTemplateDetail,searchFacebookReview } from "../../../../utils/reviewApi";
import { getSingleWidgetDetail } from "../../../../staticWidgets/utils/CommonWidgetApi";
import RatingStars from "../../../../components/RatingStars";
import ReviewPopup from "./ReviewPopup";
import BadgeWidget from "./BadgeWidget";
import FloatingWidget from "./FloatingWidget";

const CommonWidget = ({selectedLayout,isTemplateId,widgetId,reviewTabData}) => {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedReview, setSelectedReview] = useState(null);
    const [visibleGridCount, setVisibleGridCount] = useState(8);
    const [visibleListLayoutCount, setVisibleListLayoutCount] = useState(5);
    const [visibleMasonryLayoutCount, setVisibleMasonryLayoutCount] = useState(8);

    // Default values for the reviewTabData
    const {
        is_website_frontend,
        layout_is_full_width,
        business_name,
        layout_custom_width,
        style_background_color,
        header_is_widget_title,
        header_title,
        header_caption,
        setting_rating_format,
        style_widget_title_color,
        style_widget_title_fonts_title_size,
        style_widget_title_caption_color,
        style_widget_title_fonts_caption_size,
        style_header_background_color,
        header_is_show_header,
        header_write_reviews_button,
        header_source_icon,
        header_rating,
        header_number_of_reviews,
        style_header_text_color,
        style_header_write_review_background_color,
        style_header_write_review_text_color,
        header_write_review_button_type,
        style_rating_stars_color,
        layout_rows_on_mobile,
        layout_rows,
        style_load_more_button_color,
        style_load_more_button_text_color
    } = reviewTabData || {};

    // Map layout name to a specific class
    const layoutClassMap = {
        carousel_layout: 'upgadeify-carousel-style',
        list_layout: 'upgadeify-list-style',
        grid_layout: 'upgadeify-grid-style',
        masonry_layout: 'upgadeify-masonry-style',
        slider_layout: 'upgadeify-slider-style',
    };
    const layoutClass = layoutClassMap[selectedLayout] || '';

    //Helper to get slidesToShow from width
    const getSlidesToShow = (width) => {
        const w = Number(width);
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        return 1;
    };
    //State use for handle responsive logic
    const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow(layout_custom_width));

    //Update slidesToShow when window resizes or width changes
    useEffect(() => {
        const handleResize = () => {
        setSlidesToShow(getSlidesToShow(layout_custom_width));
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [layout_custom_width]);

    //Carousel Slider Settings
    const carouselSliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        arrows: true,
        autoplay: false,
        responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                dots: false,
            },
        },
        ],
    };

    //Slider Settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 3,
        arrows: false,
        autoplay: false,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                },
            },
        ],
    };

    // Fetching Review data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let result;
                if (isTemplateId) {
                    result = await getSingleReviewTemplateDetail(isTemplateId);
                } else if (widgetId) {
                    result = await getSingleWidgetDetail(widgetId);
                } else if (business_name) {
                    const payload = { business_name: business_name };
                    result = await searchFacebookReview(payload);
                } else {
                    let app_type = 'Facebook_Review';
                    result = await getDefaultReviewList(app_type);
                }
                setApiData(result); 
            } catch (error) {
                setApiData(null); 
                toast.warning(error.message);
            } finally {
                setLoading(false); 
            }
        };

        fetchData();
    }, [isTemplateId, widgetId, business_name]);

    //Handle Layout Rows and Columns
    const layoutColumns = 3;
    // Simple: detect if the screen is mobile once at render time
    const isMobile = window.innerWidth <= 768;
    const layoutRows = isMobile
        ? layout_rows_on_mobile
        : layout_rows;

    useEffect(() => {
        const newGridCount = layoutRows * layoutColumns;
        const newMasonryCount = layoutRows * layoutColumns;

        setVisibleGridCount(newGridCount);
        setVisibleListLayoutCount(layoutRows);
        setVisibleMasonryLayoutCount(newMasonryCount);
    }, [layoutRows, layoutColumns]);

    // Open popup when clicking "Read More"
    const handleReadMoreClick = (review) => {
        setSelectedReview(review);
    };

    // Close the popup
    const handleClosePopup = () => {
        setSelectedReview(null);
    };

    // Load More List Layout function
    const handleGridLoadMore = () => {
        setVisibleGridCount((prevCount) => prevCount + 8);
    };
    
    // Load List Layout More function
    const handleListLayoutLoadMore = () => {
        setVisibleListLayoutCount((prevCount) => prevCount + 5);
    };

    // Load Masonry Layout More function
    const handleMasonryLayoutLoadMore = () => {
        setVisibleMasonryLayoutCount((prevCount) => prevCount + 8);
    };

    //Handle dynamic styles based on layout
    const dynamicStyle =
        selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout'
            ? Number(layout_is_full_width) === 1
            ? {
                backgroundColor: style_background_color,
                margin: '0 auto',
            }
            : {
                backgroundColor: style_background_color,
                maxWidth: `${layout_custom_width}px`,
                margin: '0 auto',
                }
            : {};

    // Get grid layout style based on custom width
    const getGridLayoutStyle = () => {
        if (selectedLayout !== 'grid_layout') return {};

        if (layout_custom_width > 1280 && layout_custom_width < 1921) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(24%, 1fr))',
            
            };
        }

        if (layout_custom_width > 991 && layout_custom_width < 1280) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        
            };
        }

        if (layout_custom_width > 540 && layout_custom_width < 991) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(48%, 1fr))',
            
            };
        }

        if (layout_custom_width > 0 && layout_custom_width < 540) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(100%, 1fr))',
            
            };
        }

        // Default fallback
        return {
            gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        };
    };

    return (
        <div className={`upgadeify-review-template-widget upgadeify ${layoutClass} ${selectedReview ? 'carousel-popup-open' : ''} ${is_website_frontend}`} style={dynamicStyle}>
             {/* Loader and No Api Data Handling */}
            {loading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p>Loading please wait...</p>
                </div>
            ) : apiData?.status === true ? (
            <>
                {/* Show Header Section Accordgin to condition*/}
                {selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout' && (
                    <>
                    {Number(header_is_widget_title) === 1 && (
                        <>
                            <div className="upgadeify-facebook-main-title" style={{ color: style_widget_title_color,fontSize: style_widget_title_fonts_title_size }}>{header_title || ""}</div>
                            <div className="upgadeify-facebook-main-caption" style={{ color: style_widget_title_caption_color,fontSize: style_widget_title_fonts_caption_size }}>{header_caption || ""}</div>
                        </>
                    )}
                    {Number(header_is_show_header) === 1 &&
                        (Number(header_source_icon) === 1 ||
                            Number(header_rating) === 1 ||
                            Number(header_number_of_reviews) === 1 ||
                            Number(header_write_reviews_button) === 1) && (
                            <div className="upgadeify-facebook-reviews-header" style={{ backgroundColor: style_header_background_color,}}>
                        
                                <div className='upgadeify-facebook-reviews-header-left'>
                                    {Number(header_source_icon) === 1 && (
                                    <div className='upgadeify-facebook-reviews-header-title'>
                                     <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 94 36" className="injected-svg"><path fill="#0076FB" d="M63.058 13.502c4.173 0 5.834 2.674 5.834 5.999 0 3.888-2.619 5.999-5.834 5.999-3.938 0-5.834-2.357-5.834-6 0-3.43 1.463-5.998 5.834-5.998Zm12.879 0c4.172 0 5.834 2.674 5.834 5.999 0 3.888-2.619 5.999-5.834 5.999-3.938 0-5.834-2.357-5.834-6 0-3.43 1.463-5.998 5.834-5.998Zm-62.245-.159c1.373 0 2.6.704 3.486 1.83v-1.495h3.36v11.417h-3.342l.018-1.477c-.885 1.143-2.15 1.864-3.522 1.864-2.692 0-4.877-2.34-4.877-6.07 0-3.764 2.185-6.069 4.877-6.069Zm34.085-5.7-.018 7.548c.885-1.144 2.15-1.865 3.522-1.865 2.691 0 4.877 2.34 4.877 6.07 0 3.764-2.186 6.069-4.877 6.069-1.373 0-2.601-.704-3.486-1.83v1.513l-3.486-.07V8.03l3.468-.387Zm-10.188 5.77c6.495.052 5.372 6.763 5.296 7.176l-.003.02v-.017H35.44s-.163 2.04 1.878 2.216c2.023.176 4.606-.598 4.606-.598l.74 2.305s-2.42 1.196-5.599.827c-3.197-.37-5.057-1.513-5.202-5.454 0 0-.975-6.51 5.726-6.474Zm-14.035 1.355c1.97-1.759 6.358-1.583 7.803-.545l-.848 2.463s-.633-.616-2.44-.598c0 0-2.672-.159-2.672 3.22v.175c0 3.36 2.673 3.22 2.673 3.22 1.806.035 2.439-.58 2.439-.598l.848 2.463c-1.463 1.038-5.834 1.213-7.803-.546-1.698-1.513-1.698-3.993-1.662-4.627-.036-.633-.036-3.096 1.662-4.627Zm62.986-7.16V18.78l3.558-5.138h3.74l-3.65 5.736L94 25.13h-3.613l-3.847-5.84v5.84h-3.504V7.96l3.504-.352ZM6.369 7.502h2.464v2.78H7.26s-1.427-.193-1.427 1.144v2.181h2.999l-.361 3.026H5.816v8.445H2.348V16.58H0v-2.974h2.439v-2.533s-.308-1.39.975-2.533c1.233-1.1 2.834-1.045 2.955-1.039Zm8.389 8.621c-1.229 0-2.24 1.09-2.24 2.234v2.182c0 1.143.993 2.234 2.24 2.234 1.228 0 2.24-1.09 2.24-2.234v-2.182c0-1.143-.994-2.234-2.24-2.234Zm48.264-.053c-1.283 0-2.312 1.091-2.312 2.235v2.181c0 1.144 1.047 2.234 2.312 2.234 1.282 0 2.312-1.09 2.312-2.234v-2.181c0-1.144-1.048-2.235-2.312-2.235Zm12.879 0c-1.283 0-2.312 1.091-2.312 2.235v2.181c0 1.144 1.047 2.234 2.312 2.234 1.282 0 2.312-1.09 2.312-2.234v-2.181c0-1.144-1.048-2.235-2.312-2.235Zm-25.668-.017c-1.228 0-2.24 1.09-2.24 2.234v2.181c0 1.144 1.012 2.235 2.24 2.235 1.229 0 2.24-1.091 2.24-2.235v-2.181c0-1.144-1.011-2.234-2.24-2.234Zm-12.698-.282c-2.348 0-2.168 2.534-2.168 2.534h4.336s.18-2.534-2.168-2.534Z"></path></svg>
                                        <div className='upgadeify-facebook-reviews-header-title-text' style={{color: style_header_text_color,}}>Reviews</div>
                                    </div>
                                    )}
                                    <div className='upgadeify-facebook-reviews-header-rating'>
                                        {Number(header_rating) === 1 && (
                                        <>
                                            <div className="upgadeify-facebook-reviews-header-rating-text" style={{color: style_header_text_color,}}>{setting_rating_format === '4.9' ? Number(apiData.data.average_rating).toFixed(1) : Number(apiData.data.average_rating).toFixed(2) }</div>
                                            <div className="upgadeify-facebook-reviews-item-rating">
                                                <RatingStars average_rating={apiData.data.average_rating} newColor={style_rating_stars_color} />
                                            </div>
                                        </>
                                        )}
                                        {Number(header_number_of_reviews) === 1 && (
                                            <span className="upgadeify-facebook-reviews-header-number-of-reviews-text">({apiData.data.total_reviews})</span>
                                        )}
                                    </div>
                                </div>
                                {Number(header_write_reviews_button) === 1 && (
                                    <div className='upgadeify-facebook-reviews-header-right'>
                                        <a
                                            href={apiData?.data?.google_url?.startsWith('http') ? apiData.data.google_url : `https://${apiData.data.google_url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className='upgadeify-facebook-reviews-header-button'
                                            style={{color: style_header_write_review_text_color,backgroundColor: style_header_write_review_background_color,fontSize: header_write_review_button_type}}>Review us on Facebook
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/*If Carousel Layout Type*/}
                {selectedLayout === 'carousel_layout' && (
                    <Slider {...carouselSliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                        ))}
                    </Slider>
                )}

                {/*If List Layout Type*/}
                {selectedLayout === 'list_layout' && (
                    <>
                        <div className='upgadeify-list_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleListLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {/* Load More Button  */}
                        {visibleListLayoutCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleListLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Grid Layout Type*/}
                {selectedLayout === 'grid_layout' && (
                    <>
                        <div className='upgadeify-grid-layout' style={getGridLayoutStyle()}>
                            {apiData.data.review_list.slice(0, visibleGridCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {visibleGridCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleGridLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Masonry Layout Type*/}
                {selectedLayout === 'masonry_layout' && (
                    <>
                        <div className='upgadeify-masonry_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleMasonryLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                            
                        </div>
                            {/* Load More Button  */}
                            {visibleMasonryLayoutCount < apiData.data.review_list.length && (
                                <div className="load-more-button">
                                    <button onClick={handleMasonryLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                                </div>
                            )}
                    </>
                )}

                {/*If Slider Layout Type*/}
                {selectedLayout === 'slider_layout' && (
                    <Slider {...sliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData} />
                        ))}
                    </Slider>
                )}

                {/*If Badge Layout Type*/}
                {selectedLayout === 'badge_layout' && (
                    <BadgeWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*If Floating Badge Layout Type*/}
                {selectedLayout === 'floating_badge_layout' && (
                    <FloatingWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*Common Popup */}
                {selectedReview && <ReviewPopup review={selectedReview} onClose={handleClosePopup} />}
            </>
            ) : (
                <div className="no-inner-result-found">
                    <p>No Facebook reviews found please try.</p>
                </div>
            )}
        </div >
    );
};

const ReviewLoopItem = ({ review, handleReadMoreClick, reviewTabData }) => (
    <>
        <div className="upgadeify-facebook-reviews">
            <div className='upgadeify-facebook-reviews-item' style={{ padding: `${reviewTabData.layout_item_spacing}px`, backgroundColor: reviewTabData.style_review_background_color,}}>
                <div className='upgadeify-facebook-reviews-head'>
                    {(Number(reviewTabData.review_is_show_reviewer_picture) === 1 || Number(reviewTabData.review_is_show_verified_badge) === 1) && (
                        <div className="upgadeify-facebook-reviews-item-image">
                            {review.image ? (
                                <img src={review.image} alt="Review" />
                            ) : (
                                <div className="fallback-avatar">
                                    {review.name.charAt(0)}
                                </div>
                            )}
                           
                        </div>
                    )}
                    <div className='upgadeify-facebook-reviews-item-name'>
                        {Number(reviewTabData.review_is_show_reviewer_name) === 1 && (
                        Number(reviewTabData.setting_is_enable_external_links) === 1 && review.author_url ? ( 
                                <a
                                    href={review.author_url} 
                                    target={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "_blank" : "_self"}
                                    rel={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "noopener noreferrer" : undefined}
                                    style={{ color: reviewTabData.style_review_reviewer_name_color }}
                                >
                                    <span className="upgadeify-facebook-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>{review.name}</span>
                                </a>
                            ) : (
                                <span className="upgadeify-facebook-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>
                                    {review.name}
                                </span>
                            )
                        )}

                        {Number(reviewTabData.review_is_show_verified_badge) === 1 && (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" className="review-verified-badge-icon" aria-hidden="true">
                                <path fill={reviewTabData.style_review_verified_badge_color} d="M6.757.236a.35.35 0 0 1 .486 0l1.106 1.07a.35.35 0 0 0 .329.089l1.493-.375a.35.35 0 0 1 .422.244l.422 1.48a.35.35 0 0 0 .24.24l1.481.423a.35.35 0 0 1 .244.422l-.375 1.493a.35.35 0 0 0 .088.329l1.071 1.106a.35.35 0 0 1 0 .486l-1.07 1.106a.35.35 0 0 0-.089.329l.375 1.493a.35.35 0 0 1-.244.422l-1.48.422a.35.35 0 0 0-.24.24l-.423 1.481a.35.35 0 0 1-.422.244l-1.493-.375a.35.35 0 0 0-.329.088l-1.106 1.071a.35.35 0 0 1-.486 0l-1.106-1.07a.35.35 0 0 0-.329-.089l-1.493.375a.35.35 0 0 1-.422-.244l-.422-1.48a.35.35 0 0 0-.24-.24l-1.481-.423a.35.35 0 0 1-.244-.422l.375-1.493a.35.35 0 0 0-.088-.329L.236 7.243a.35.35 0 0 1 0-.486l1.07-1.106a.35.35 0 0 0 .089-.329L1.02 3.829a.35.35 0 0 1 .244-.422l1.48-.422a.35.35 0 0 0 .24-.24l.423-1.481a.35.35 0 0 1 .422-.244l1.493.375a.35.35 0 0 0 .329-.088L6.757.236Z"></path><path fill="#fff" fill-rule="evenodd" d="M9.065 4.85a.644.644 0 0 1 .899 0 .615.615 0 0 1 .053.823l-.053.059L6.48 9.15a.645.645 0 0 1-.84.052l-.06-.052-1.66-1.527a.616.616 0 0 1 0-.882.645.645 0 0 1 .84-.052l.06.052 1.21 1.086 3.034-2.978Z" clip-rule="evenodd"></path>
                            </svg>
                        )}

                        <span className="upgadeify-facebook-reviews-item-date">
                            {Number(reviewTabData.review_is_show_date) === 1 && (
                                <span style={{color: reviewTabData.style_review_date_color,}}>{review.review_date ? format(new Date(review.review_date), 'yyyy-MM-dd') : ''}</span>
                            )}
                        </span>
                    </div>
                </div>
                <div className="upgadeify-facebook-reviews-item-content">
                   {Number(reviewTabData.review_is_show_rating) === 1 && (
                        <span className="upgadeify-facebook-reviews-item-rating" style={{ fontSize: reviewTabData.style_review_rating_size }}>
                            <RatingStars average_rating={review.rating} newColor={reviewTabData.style_rating_stars_color}/>
                        </span>
                    )}
                    <div className="upgadeify-facebook-reviews-item-text" style={{color: `${reviewTabData.style_review_text_color}`,fontSize: reviewTabData.style_review_text_font_size}}>
                        {review.description.length > 50
                        ? `${review.description.slice(0, 50)}...`
                        : review.description}
                    </div>
                    {review.description.length > 50 && (
                        <div className='upgadeify-facebook-reviews-readmore' onClick={() => handleReadMoreClick(review)} style={{color: reviewTabData.style_read_more_color,fontSize: reviewTabData.style_review_text_font_size}}>
                                Read more
                        </div>
                    )}

                    {Number(reviewTabData.review_is_show_review_source) === 1 && (  
                        <div className='upgadeify-facebook-reviews-item-image-badge'>
                           <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="injected-svg"><circle cx="12" cy="12" r="10" fill="#0076FB"></circle><path fill="#fff" fill-rule="evenodd" d="M16.167 6.167v2.639h-1.842a1.04 1.04 0 0 0-1.048 1.03v2.04h2.843l-.393 2.935h-2.45v7.108a10.113 10.113 0 0 1-2.982-.064v-7.044H7.833v-2.934h2.462V9.412c0-1.792 1.477-3.245 3.298-3.245h2.574Z" clip-rule="evenodd"></path></svg>
                            Posted On   
                            <> <a href={`https://www.facebook.com`} target="blank">Facebook</a></> 
                        </div> 
                    )} 
                </div>
            </div>
        </div>
    </>
);

export default CommonWidget;
